import { isElectron, electronAPI } from '../utils/electronBridge';
import screenwriterDB from '../storage/indexedDB';
import { generateLocalSheetXlsx, parseLocalSheetXlsx } from '../utils/localSheetXlsx';

const PROJECT_FOLDER_KEY = 'tarrProjectFolder';
const SYNC_MODE_KEY = 'tarrSyncMode';
const FOLDER_PROMPTED_KEY = 'tarrProjectFolderPrompted';
const MAX_VERSIONS = 30;
const SHARED_PROJECT_FOLDER = 'Shared with me';
// Live edits are written to the file itself on every autosave; the versions/
// backup snapshots are throttled to this interval so ~2s autosaves don't push
// real history out of the MAX_VERSIONS window within a minute.
const VERSION_SNAPSHOT_MIN_INTERVAL_MS = 5 * 60 * 1000;

// A production sheet's grid content has no real per-language concept the way
// a screenplay/book/shortform (or a production kind:'doc') file does -- its
// `content` field is a single {headers, rows, merges} JSON blob
// (SpreadsheetEditor.jsx's own shape). Detected the same way MainSidebar.jsx
// already does everywhere else this distinction matters.
function isSheetKind(templateId) {
  const id = typeof templateId === 'string' ? templateId
    : (templateId && typeof templateId === 'object' ? templateId.id : null);
  return id === 'sheet';
}

// On-disk layout under {projectFolder}/tarr-projects/:
//
//   {ProjectName}/                    <- one super folder per project
//     {FileName}/                     <- one folder per file
//       meta.tarr                     <- file-level fields (no content)
//       languages/
//         en.tarr                     <- live content, one file per language
//         es.tarr                     <- (prose content -- screenplay/book/
//                                         shortform/production "doc")
//         -- or, for a production "sheet" kind, one REAL .xlsx per language
//         en.xlsx                        instead of .tarr -- base64-encoded
//         es.xlsx                        through the same encrypted-by-
//                                         default fs:write-file/read-file
//                                         path every other local file uses;
//                                         see openspec/changes/local-
//                                         production-sheets-and-path-mentions
//       versions/
//         2026-07-12T10-30-00-000Z/   <- one snapshot folder per save
//           meta.tarr
//           languages/
//         ...                         <- only the 30 most recent kept

class LocalFileService {
  constructor() {
    this.projectFolder = localStorage.getItem(PROJECT_FOLDER_KEY) || null;
    this.saveTimers = new Map(); // fileId -> timeout
    this._saveChains = new Map(); // fileId -> promise of the latest in-flight save
    this._lastVersionAt = new Map(); // fileId -> ms timestamp of the last versions/ snapshot
    this._projectIdByName = new Map(); // projectName (lowercased) -> project id, cached per session

    // Restore the allowed project folder in the main process on startup, and
    // resume watching it — without this, a restart with an already-configured
    // folder never re-arms fs:watch-folder, and external edits go unnoticed
    // until the user reselects the folder from settings.
    if (isElectron && this.projectFolder && electronAPI.fs.setProjectFolder) {
      this._ready = electronAPI.fs.setProjectFolder(this.projectFolder)
        .then(() => electronAPI.fs.watchFolder(`${this.projectFolder}/tarr-projects`))
        .catch(() => {});
    } else {
      this._ready = Promise.resolve();
    }
  }

  /** Wait for the sandbox folder to be restored before any file operation. */
  async _ensureReady() {
    await this._ready;
  }

  isAvailable() {
    return isElectron;
  }

  getSyncMode() {
    if (!isElectron) return 'cloud';
    return localStorage.getItem(SYNC_MODE_KEY) || 'both';
  }

  setSyncMode(mode) {
    localStorage.setItem(SYNC_MODE_KEY, mode);
  }

  getProjectFolder() {
    return this.projectFolder;
  }

  // First-run setup: with no project folder yet, ask once (native dialog,
  // opening in Documents) where to keep local copies -- the folder is what
  // turns on the local half of the hybrid cloud + local setup, and the
  // sandbox only allows a folder the user picked in a dialog. Asked at most
  // once per install so cancelling isn't nagged; Settings -> Storage & Sync
  // can always choose one later. Returns the folder path if one was set.
  async promptForProjectFolderIfNeeded() {
    if (!isElectron || this.projectFolder || this.getSyncMode() === 'cloud') return null;
    if (localStorage.getItem(FOLDER_PROMPTED_KEY)) return null;
    localStorage.setItem(FOLDER_PROMPTED_KEY, 'true');

    const result = await electronAPI.fs.chooseFolder({
      useDocumentsDefault: true,
      title: 'Choose where tarr.ink keeps your project files',
    });
    if (!result || !result.success || !result.path) return null;
    return this.setProjectFolder(result.path);
  }

  // Writes one already-known file (by id) to the project folder, resolving
  // its name/project from IndexedDB the same way version browsing does.
  async saveFileLocallyById(fileId) {
    const file = await this._resolveFileForPath(fileId);
    return file ? this.saveFileLocally(file) : false;
  }

  needsProjectFolder() {
    return isElectron && !this.projectFolder;
  }

  async setProjectFolder(folderPath) {
    if (!isElectron) return null;

    let selectedPath = folderPath;
    if (!selectedPath) {
      const result = await electronAPI.fs.chooseFolder();
      if (!result || !result.success || !result.path) return null;
      selectedPath = result.path;
    }

    const projectsPath = selectedPath + '/tarr-projects';
    await electronAPI.fs.ensureDir(projectsPath);

    this.projectFolder = selectedPath;
    localStorage.setItem(PROJECT_FOLDER_KEY, selectedPath);

    await electronAPI.fs.watchFolder(projectsPath);

    return selectedPath;
  }

  // --- Path helpers -----------------------------------------------------

  _sanitize(name) {
    return (name || 'untitled').replace(/[^a-zA-Z0-9-_. ]/g, '_');
  }

  _getProjectFolderPath(projectName) {
    if (!this.projectFolder) return null;
    return `${this.projectFolder}/tarr-projects/${this._sanitize(projectName || 'default').replace(/ /g, '_')}`;
  }

  _getFileFolderPath(file) {
    const projectPath = this._getProjectFolderPath(file.projectName || file.project_name);
    if (!projectPath) return null;
    const fileName = this._sanitize(file.name || file.title || file.id);
    return `${projectPath}/${fileName}`;
  }

  // Public wrapper around _resolveFileForPath + _getFileFolderPath (the
  // same two-step resolution listVersions/getVersionLanguageContent below
  // already use) — for chat-surface callers (StreamingStepsChain.jsx) that
  // need to show WHERE a local-only file actually lives on disk, since
  // there's no cloud URL to link to the way a normal file gets. Returns
  // null outside Electron, with no project folder set, or for a file this
  // isn't actually tracking locally — callers should fall back to the
  // normal in-app open link in all of those cases, not show a broken path.
  async getLocalFilePath(fileId) {
    if (!isElectron || !this.projectFolder) return null;
    const file = await this._resolveFileForPath(fileId);
    if (!file) return null;
    return this._getFileFolderPath(file);
  }

  // Version-browsing APIs (VersionHistoryPanel) take a bare fileId — resolve
  // the {name, projectName} needed for path-building from IndexedDB so the
  // panel doesn't need to thread extra props through RightSidebar.
  async _resolveFileForPath(fileId) {
    const dbFile = await screenwriterDB.getFile(fileId);
    if (!dbFile) return null;
    let projectName = 'default';
    if (dbFile.is_shared) {
      // A file shared with this user belongs to someone else's project, which
      // doesn't exist here -- keep all of them together in one folder rather
      // than mixing them into 'default'.
      projectName = SHARED_PROJECT_FOLDER;
    } else if (dbFile.project_id) {
      try {
        const project = await screenwriterDB.getProject(dbFile.project_id);
        if (project?.name) projectName = project.name;
      } catch { /* fall back to default */ }
    }
    return { id: fileId, name: dbFile.name, projectName };
  }

  // --- Snapshot building --------------------------------------------------

  // Full multi-language snapshot for one file, in the same {file, languages}
  // shape fileExportService's .tarr download/import already uses — so local
  // auto-saves and external-change re-import all speak one format.
  async _buildSnapshot(fileId, fallback = {}) {
    const dbFile = await screenwriterDB.getFile(fileId);
    const languages = dbFile ? await screenwriterDB.getFileLanguages(fileId) : [];

    return {
      file: {
        id: fileId,
        name: dbFile?.name || fallback.name || fallback.title || 'Untitled',
        template_id: dbFile?.template_id || fallback.mode || fallback.template_id || 'screenplay',
        title_page: dbFile?.title_page || {},
        content: dbFile?.content || '',
        pinned_locations: dbFile?.pinned_locations || {},
        annotations: dbFile?.annotations || {},
        default_language: dbFile?.default_language || fallback.language || 'en',
        available_languages: dbFile?.available_languages || ['en'],
        project_id: dbFile?.project_id || fallback.projectId || fallback.project_id || null,
        projectName: fallback.projectName || fallback.project_name || 'default',
        created: dbFile?.created || new Date().toISOString(),
        last_modified: new Date().toISOString(),
      },
      languages: (languages || []).map(lang => ({
        language_code: lang.language_code,
        language_name: lang.language_name,
        content: lang.content,
        title_page: lang.title_page,
        annotations: lang.annotations,
        pinned_locations: lang.pinned_locations,
        last_modified: lang.last_modified,
      })),
      exportedAt: new Date().toISOString(),
      version: '3.0', // folder-per-file + versions/ layout
    };
  }

  // --- Save (live state + a version snapshot) -----------------------------

  // Saves for the same file run one after another: autosaves fire every ~2s
  // and each writes several files, so overlapping writes could interleave.
  saveFileLocally(file) {
    if (!isElectron || !this.projectFolder) return Promise.resolve(false);
    const previous = this._saveChains.get(file.id) || Promise.resolve();
    const run = previous.catch(() => {}).then(() => this._saveFileLocallyNow(file));
    this._saveChains.set(file.id, run);
    run.finally(() => {
      if (this._saveChains.get(file.id) === run) this._saveChains.delete(file.id);
    }).catch(() => {});
    return run;
  }

  async _saveFileLocallyNow(file) {
    if (!isElectron || !this.projectFolder) return false;
    await this._ensureReady();

    const fileFolder = this._getFileFolderPath(file);
    if (!fileFolder) return false;

    try {
      const snapshot = await this._buildSnapshot(file.id, file);
      await this._writeSnapshotTo(fileFolder, snapshot);

      // A brand-new file autosaves every ~2s while still empty (nothing typed
      // yet) -- without this guard every one of those ticks piled up its own
      // versions/<timestamp> snapshot with the same empty content. Write
      // exactly one baseline snapshot for an empty file, then stop until it
      // actually has content.
      const hasContent = snapshot.languages.some(l => (l.content || '').trim().length > 0);
      const lastVersionAt = this._lastVersionAt.get(file.id) || 0;
      const versionDue = Date.now() - lastVersionAt >= VERSION_SNAPSHOT_MIN_INTERVAL_MS;
      if ((hasContent && versionDue) || !(await this._hasAnyVersion(fileFolder))) {
        await this._writeVersionSnapshot(fileFolder, snapshot);
        this._lastVersionAt.set(file.id, Date.now());
      }
      return true;
    } catch (err) {
      console.error('[LocalFileService] Save failed:', err);
      return false;
    }
  }

  async _hasAnyVersion(fileFolder) {
    const listing = await electronAPI.fs.listDirectory(`${fileFolder}/versions`).catch(() => null);
    if (!listing?.success || !listing.data) return false;
    return listing.data.some(e => e.isDirectory);
  }

  async _writeSnapshotTo(folder, snapshot) {
    await electronAPI.fs.writeFile(
      `${folder}/meta.tarr`,
      JSON.stringify({ file: snapshot.file, exportedAt: snapshot.exportedAt, version: snapshot.version }, null, 2)
    );

    const languagesFolder = `${folder}/languages`;
    await electronAPI.fs.ensureDir(languagesFolder);

    if (isSheetKind(snapshot.file.template_id)) {
      // A sheet gets one real .xlsx PER LANGUAGE, same as prose content gets
      // one .tarr per language -- production sheets support per-language
      // grids the same way screenplay/book/shortform support per-language
      // prose. Falls back to the file's own default-language content when
      // there are no language rows yet at all (a brand-new sheet), same
      // defensive posture the prose path already has for that case.
      const langs = snapshot.languages.length > 0
        ? snapshot.languages
        : [{ language_code: snapshot.file.default_language || 'en', content: snapshot.file.content }];
      for (const lang of langs) {
        if (!lang.language_code) continue;
        const xlsxBytes = generateLocalSheetXlsx({ name: snapshot.file.name, content: lang.content });
        // Base64-encoded through the SAME default (no explicit `encoding`
        // argument) fs:write-file path every other local file uses, so it
        // gets encrypted exactly like they do -- see main.js's
        // encryptForDisk, which only bypasses encryption when a caller
        // explicitly asks for a different encoding.
        const base64 = Buffer.from(xlsxBytes).toString('base64');
        await electronAPI.fs.writeFile(`${languagesFolder}/${lang.language_code}.xlsx`, base64);
      }
      return;
    }

    for (const lang of snapshot.languages) {
      if (!lang.language_code) continue;
      await electronAPI.fs.writeFile(`${languagesFolder}/${lang.language_code}.tarr`, JSON.stringify(lang, null, 2));
    }
  }

  async _writeVersionSnapshot(fileFolder, snapshot) {
    const stamp = new Date().toISOString().replace(/[:.]/g, '-'); // filesystem-safe
    const versionFolder = `${fileFolder}/versions/${stamp}`;
    await this._writeSnapshotTo(versionFolder, snapshot);
    await this._pruneOldVersions(`${fileFolder}/versions`);
  }

  // ISO timestamp folder names sort lexicographically = chronologically, so
  // the oldest names are simply the ones sorted first.
  async _pruneOldVersions(versionsFolder) {
    const listing = await electronAPI.fs.listDirectory(versionsFolder);
    if (!listing?.success || !listing.data) return;

    const versionDirs = listing.data.filter(e => e.isDirectory).map(e => e.name).sort();
    const excess = versionDirs.length - MAX_VERSIONS;
    if (excess <= 0) return;

    for (const dirName of versionDirs.slice(0, excess)) {
      await electronAPI.fs.deleteDir(`${versionsFolder}/${dirName}`);
    }
  }

  // Debounced save - call this on content changes
  saveFileLocallyDebounced(file, delay = 2000) {
    if (!isElectron || !this.projectFolder) return;

    const fileId = file.id;
    if (this.saveTimers.has(fileId)) {
      clearTimeout(this.saveTimers.get(fileId));
    }

    this.saveTimers.set(fileId, setTimeout(() => {
      this.saveTimers.delete(fileId);
      this.saveFileLocally(file);
    }, delay));
  }

  // --- Load ----------------------------------------------------------------

  async _readSnapshotFromFolder(fileFolder, projectFolderName) {
    const metaResult = await electronAPI.fs.readFile(`${fileFolder}/meta.tarr`);
    if (!metaResult?.success) return null;
    const meta = JSON.parse(metaResult.data);

    const languages = [];
    const isSheet = isSheetKind(meta.file?.template_id);
    const langListing = await electronAPI.fs.listDirectory(`${fileFolder}/languages`);
    if (langListing?.success && langListing.data) {
      for (const entry of langListing.data) {
        if (!entry.isFile) continue;
        if (isSheet) {
          if (!entry.name.endsWith('.xlsx')) continue;
          const languageCode = entry.name.slice(0, -'.xlsx'.length);
          const sheetResult = await electronAPI.fs.readFile(`${fileFolder}/languages/${entry.name}`);
          if (!sheetResult?.success) continue;
          try {
            const xlsxBytes = Buffer.from(sheetResult.data, 'base64');
            const { headers, rows } = parseLocalSheetXlsx(xlsxBytes);
            languages.push({
              language_code: languageCode,
              language_name: null,
              content: JSON.stringify({ headers, rows, merges: {} }),
              title_page: null,
              annotations: null,
              pinned_locations: null,
              last_modified: meta.exportedAt,
            });
          } catch { /* skip corrupt/unreadable sheet -- same tolerance as a corrupt .tarr language file */ }
        } else {
          if (!entry.name.endsWith('.tarr')) continue;
          const langResult = await electronAPI.fs.readFile(`${fileFolder}/languages/${entry.name}`);
          if (langResult?.success) {
            try { languages.push(JSON.parse(langResult.data)); } catch { /* skip corrupt file */ }
          }
        }
      }
    }

    return {
      file: { ...meta.file, projectName: meta.file.projectName || projectFolderName },
      languages,
      exportedAt: meta.exportedAt,
      version: meta.version || '3.0',
    };
  }

  // Read every file folder under tarr-projects/{project}/. Returns the same
  // {file, languages} snapshot shape saveFileLocally writes.
  async loadLocalFiles() {
    if (!isElectron || !this.projectFolder) return [];
    await this._ensureReady();

    const projectsPath = `${this.projectFolder}/tarr-projects`;

    try {
      const projectsResult = await electronAPI.fs.listDirectory(projectsPath);
      if (!projectsResult?.success || !projectsResult.data) return [];

      const snapshots = [];
      for (const project of projectsResult.data) {
        if (!project.isDirectory) continue;
        const projectPath = `${projectsPath}/${project.name}`;
        const filesResult = await electronAPI.fs.listDirectory(projectPath);
        if (!filesResult?.success || !filesResult.data) continue;

        for (const fileEntry of filesResult.data) {
          if (!fileEntry.isDirectory) continue; // each file is a folder now
          try {
            const snapshot = await this._readSnapshotFromFolder(`${projectPath}/${fileEntry.name}`, project.name);
            if (snapshot) snapshots.push(snapshot);
          } catch (err) {
            console.warn('[LocalFileService] Failed to read file folder:', fileEntry.name, err);
          }
        }
      }

      return snapshots;
    } catch (err) {
      console.error('[LocalFileService] Load failed:', err);
      return [];
    }
  }

  async _getOrCreateProjectId(projectName) {
    const key = (projectName || 'default').toLowerCase();
    if (this._projectIdByName.has(key)) return this._projectIdByName.get(key);

    const projects = await screenwriterDB.listProjects();
    const existing = projects.find(p => (p.name || '').toLowerCase() === key);
    if (existing) {
      this._projectIdByName.set(key, existing.id);
      return existing.id;
    }

    const created = await screenwriterDB.createProject({ name: projectName || 'default' });
    this._projectIdByName.set(key, created.id);
    return created.id;
  }

  // Merge one {file, languages} snapshot into IndexedDB: create the file (and
  // its project, if new) or update it and upsert every language it contains.
  async _mergeSnapshot(snapshot) {
    const { file, languages } = snapshot;
    if (!file?.id) return null;

    const projectId = file.project_id || await this._getOrCreateProjectId(file.projectName);
    const existing = await screenwriterDB.getFile(file.id);

    if (existing) {
      await screenwriterDB.updateFile(file.id, {
        name: file.name,
        template_id: file.template_id,
        title_page: file.title_page,
        pinned_locations: file.pinned_locations,
        annotations: file.annotations,
        default_language: file.default_language,
        available_languages: file.available_languages,
        project_id: existing.project_id || projectId,
        last_modified: file.last_modified,
      });
    } else {
      await screenwriterDB.createFile({
        id: file.id,
        project_id: projectId,
        name: file.name,
        template_id: file.template_id,
        title_page: file.title_page,
        pinned_locations: file.pinned_locations,
        annotations: file.annotations,
        default_language: file.default_language,
        available_languages: file.available_languages,
        storageMode: 'local',
      });
    }

    for (const lang of (languages || [])) {
      if (!lang.language_code) continue;
      await screenwriterDB.saveFileInLanguage(
        file.id,
        lang.language_code,
        lang.content || '',
        lang.title_page || null,
        { annotations: lang.annotations, pinned_locations: lang.pinned_locations }
      );
    }

    return file.id;
  }

  // Re-read a changed file's folder (path from the fs:watch-folder event) and
  // merge it into IndexedDB. versions/ changes are historical, not live state,
  // so they're ignored. Returns { fileId, changedLanguages: [{languageCode,
  // content}] } for what actually differs from what IndexedDB already holds,
  // or null if nothing changed / it was unreadable. "Nothing changed" is the
  // normal result of the app's OWN write to disk echoing back through the
  // watcher -- skipping it keeps that echo from re-merging, reloading the
  // open editor mid-typing, or re-pushing content that was just saved.
  async importExternalFileDetailed(filePath) {
    if (!isElectron) return null;
    if (filePath.includes('/versions/')) return null;

    try {
      const parts = filePath.split('/');
      const languagesIdx = parts.lastIndexOf('languages');
      if (languagesIdx === -1) return null; // only language-file changes are reconciled live

      const fileFolder = parts.slice(0, languagesIdx).join('/');
      const projectFolderName = parts[languagesIdx - 2];

      const snapshot = await this._readSnapshotFromFolder(fileFolder, projectFolderName);
      if (!snapshot?.file?.id) return null;

      const changedLanguages = [];
      for (const lang of (snapshot.languages || [])) {
        if (!lang.language_code) continue;
        const existing = await screenwriterDB.getFileInLanguage(snapshot.file.id, lang.language_code);
        if (!existing || (existing.content || '') !== (lang.content || '')) {
          changedLanguages.push({ languageCode: lang.language_code, content: lang.content || '' });
        }
      }
      if (changedLanguages.length === 0) return null;

      const fileId = await this._mergeSnapshot(snapshot);
      return fileId ? { fileId, changedLanguages } : null;
    } catch (err) {
      console.warn('[LocalFileService] Failed to import external change:', filePath, err);
      return null;
    }
  }

  async importExternalFile(filePath) {
    const result = await this.importExternalFileDetailed(filePath);
    return result ? result.fileId : null;
  }

  async deleteLocalFile(file) {
    if (!isElectron || !this.projectFolder) return false;
    const fileFolder = this._getFileFolderPath(file);
    if (!fileFolder) return false;
    const result = await electronAPI.fs.deleteDir(fileFolder);
    return !!result?.success;
  }

  // --- Local version history (backs VersionHistoryPanel's "Local Backups" tab) --

  // Lists the timestamped snapshots under versions/, newest first, in a shape
  // shape-compatible with the git-based versionHistoryService entries
  // (id, version, created_at, author_name, change_summary, is_base_version)
  // so VersionHistoryPanel can render either source through the same UI.
  async listVersions(fileId) {
    if (!isElectron || !this.projectFolder) return [];
    const file = await this._resolveFileForPath(fileId);
    if (!file) return [];
    const fileFolder = this._getFileFolderPath(file);
    if (!fileFolder) return [];

    const versionsFolder = `${fileFolder}/versions`;
    const listing = await electronAPI.fs.listDirectory(versionsFolder);
    if (!listing?.success || !listing.data) return [];

    const stamps = listing.data.filter(e => e.isDirectory).map(e => e.name).sort(); // oldest -> newest
    const versions = [];
    for (let i = 0; i < stamps.length; i++) {
      const stamp = stamps[i];
      let createdAt = null;
      try {
        const metaResult = await electronAPI.fs.readFile(`${versionsFolder}/${stamp}/meta.tarr`);
        if (metaResult?.success) createdAt = JSON.parse(metaResult.data).exportedAt;
      } catch { /* fall through to fallback below */ }

      versions.push({
        id: stamp,
        version: i + 1, // ascending, oldest = 1 (matches the git-based numbering convention)
        created_at: createdAt || new Date().toISOString(),
        author_name: 'Local backup',
        change_summary: null,
        is_base_version: i === 0,
        isLocal: true,
      });
    }
    return versions.reverse(); // newest first, matching how the panel lists git history
  }

  async getVersionLanguageContent(fileId, versionId, languageCode) {
    if (!isElectron || !this.projectFolder) return null;
    const file = await this._resolveFileForPath(fileId);
    if (!file) return null;
    const fileFolder = this._getFileFolderPath(file);
    if (!fileFolder) return null;

    const result = await electronAPI.fs.readFile(`${fileFolder}/versions/${versionId}/languages/${languageCode}.tarr`);
    if (!result?.success) return null;
    try { return JSON.parse(result.data); } catch { return null; }
  }

  // Writes a local version's content for one language back into IndexedDB as
  // the live state — the "revert" action for local backups. The next autosave
  // naturally records a fresh version snapshot reflecting the restore.
  async restoreVersion(fileId, versionId, languageCode) {
    const versionLang = await this.getVersionLanguageContent(fileId, versionId, languageCode);
    if (!versionLang) throw new Error('Local version content not found');

    await screenwriterDB.saveFileInLanguage(
      fileId,
      languageCode,
      versionLang.content || '',
      versionLang.title_page || null,
      { annotations: versionLang.annotations, pinned_locations: versionLang.pinned_locations }
    );
    return true;
  }

  // Listen for external file changes (edits made outside the app to files in
  // the watched project folder). Returns an unsubscribe function.
  onExternalChange(callback) {
    if (!isElectron) return () => {};
    return electronAPI.fs.onExternalChange(callback);
  }
}

const localFileService = new LocalFileService();
export default localFileService;
